import QRCode from 'qrcode';

import voyaxMarkSvg from '@/assets/voyax-mark.svg?raw';

/**
 * Print-ready QR codes for admin-made short links, with the Voyax mark in the
 * centre.
 *
 * Scannability rules:
 *  - error correction H (~30% of the code can be covered and still read)
 *  - the logo covers at most ~22% of the width, and the modules under it are
 *    removed rather than drawn over, so phones don't misread partial dots
 *  - a 4-module white quiet zone, which scanners need around the code
 *  - dark navy on white: high contrast, never inverted
 */

export const QR_FOREGROUND = '#1B3A4B';
export const QR_BACKGROUND = '#FFFFFF';
const QUIET_ZONE = 4;
const LOGO_FRACTION = 0.22;

export interface QrOptions {
  logo?: boolean;
}

/** The patient site QR codes point at, for this admin environment. */
export function patientSiteUrl(): string {
  const configured = import.meta.env.VITE_PATIENT_SITE_URL;
  if (configured) return configured.replace(/\/$/, '');
  // Fallback: the prod API serves voyaxhealth.com; everything else is dev.
  return /\/prod\/?$/.test(import.meta.env.VITE_API_URL || '')
    ? 'https://voyaxhealth.com'
    : 'https://dev.voyaxhealth.com';
}

export function isProductionSite(): boolean {
  return patientSiteUrl() === 'https://voyaxhealth.com';
}

export function shortLinkUrl(slug: string): string {
  return `${patientSiteUrl()}/go/${slug}/`;
}

function markInner(): { viewBox: string; body: string } {
  const viewBox = voyaxMarkSvg.match(/viewBox="([^"]+)"/)?.[1] ?? '0 0 100 100';
  const body = voyaxMarkSvg
    .replace(/^[\s\S]*?<svg[^>]*>/, '')
    .replace(/<\/svg>\s*$/, '')
    .replace(/<!--[\s\S]*?-->/g, '');
  return { viewBox, body };
}

/** An SVG document (vector, scales to any print size). */
export function buildQrSvg(text: string, options: QrOptions = {}): string {
  const logo = options.logo ?? true;
  const qr = QRCode.create(text, { errorCorrectionLevel: logo ? 'H' : 'M' });
  const count = qr.modules.size;
  const total = count + QUIET_ZONE * 2;

  // Modules hidden behind the logo, plus a one-module margin.
  let clear = { x0: -1, x1: -1, y0: -1, y1: -1 };
  let logoMarkup = '';
  if (logo) {
    const side = Math.ceil(count * LOGO_FRACTION) | 1; // odd, so it centres on a module
    const start = Math.floor((count - side) / 2);
    clear = { x0: start - 1, x1: start + side, y0: start - 1, y1: start + side };
    const { viewBox, body } = markInner();
    const [, , vbW, vbH] = viewBox.split(/\s+/).map(Number);
    const box = side * 0.9;
    const scale = Math.min(box / vbW, box / vbH);
    const w = vbW * scale;
    const h = vbH * scale;
    const cx = QUIET_ZONE + start + side / 2;
    const cy = QUIET_ZONE + start + side / 2;
    logoMarkup =
      `<rect x="${QUIET_ZONE + start - 0.5}" y="${QUIET_ZONE + start - 0.5}" width="${side + 1}" height="${side + 1}" rx="${(side + 1) * 0.22}" fill="${QR_BACKGROUND}"/>` +
      `<svg x="${cx - w / 2}" y="${cy - h / 2}" width="${w}" height="${h}" viewBox="${viewBox}">${body}</svg>`;
  }

  let d = '';
  for (let y = 0; y < count; y++) {
    for (let x = 0; x < count; x++) {
      if (!qr.modules.get(x, y)) continue;
      if (x >= clear.x0 && x <= clear.x1 && y >= clear.y0 && y <= clear.y1) continue;
      d += `M${x + QUIET_ZONE} ${y + QUIET_ZONE}h1v1h-1z`;
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges">` +
    `<rect width="${total}" height="${total}" fill="${QR_BACKGROUND}"/>` +
    `<path d="${d}" fill="${QR_FOREGROUND}"/>` +
    logoMarkup +
    `</svg>`
  );
}

/** Rasterise for a PNG download (default 2048 px: sharp up to ~17 cm at 300 dpi). */
export async function svgToPngBlob(svg: string, size = 2048): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.width = size;
    img.height = size;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not render the QR code'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, size, size);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG export failed'))), 'image/png'),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
